// TanStack Query wrappers for the user-facing support surface.
//
// Cache lives in Query, not component state. Mutations invalidate the list
// + the affected detail so a new ticket / reply shows up without a manual
// refresh.

import {
  type TicketCreated,
  type TicketDetail,
  type TicketsPage,
  createTicket,
  getTicket,
  listTickets,
  replyToTicket,
} from '@/lib/support';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

const SUPPORT_KEY = ['support'] as const;

export function supportListKey(cursor?: string) {
  return [...SUPPORT_KEY, 'list', { cursor: cursor ?? null }] as const;
}

export function supportDetailKey(id: string) {
  return [...SUPPORT_KEY, 'detail', id] as const;
}

export function useTickets(cursor?: string) {
  return useQuery<TicketsPage, AxiosError>({
    queryKey: supportListKey(cursor),
    queryFn: () => listTickets({ cursor, limit: 20 }),
    refetchOnWindowFocus: false,
    // Keep the previous page on screen while the next cursor loads so the
    // list doesn't flash empty between fetches.
    placeholderData: (prev) => prev,
  });
}

export function useTicket(id: string | undefined) {
  return useQuery<TicketDetail, AxiosError>({
    queryKey: supportDetailKey(id ?? ''),
    queryFn: () => getTicket(id as string),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation<TicketCreated, AxiosError, { subject: string; body: string }>({
    mutationFn: (vars) => createTicket(vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...SUPPORT_KEY, 'list'] });
    },
  });
}

export function useReplyToTicket(id: string) {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (body) => replyToTicket(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: supportDetailKey(id) });
      qc.invalidateQueries({ queryKey: [...SUPPORT_KEY, 'list'] });
    },
  });
}
